// Phase SE-4 — useSlipExtraction hook.
//
// Mirrors useSlipAutopsy's queue-first pattern but for the Slip
// Extractor's verb. Per spec §7 SE-4, this hook ships the foundation:
// extract action → returns extracted_legs. SE-5/6/7 extend it with
// scoring, intent parsing, and execution.
//
// State machine (visible via `state`):
//   idle          — fresh mount, nothing submitted
//   extracting    — Edge fn 'extract' in-flight
//   extracted     — Edge fn returned extracted_legs (SE-4 terminal
//                   success state; SE-5 will add 'scoring' →
//                   'planning' → 'executing' → 'complete')
//   queued        — Edge fn failed (timeout/vendor); N01 graceful
//   error_input   — Local validation failed (slip missing, prompt
//                   too short, signed out)
//
// N01: never surface raw Edge errors. The 'queued' state shows a
// reassuring "Analysis queued — we'll notify you when complete." UI.

import { useCallback, useMemo, useRef, useState } from "react";

import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { applyFilterPlan, type FilterResult } from "./filterExecutor.ts";
import type { FilterPlan } from "./intentParser.ts";
import type { StressTestReport } from "./stressTest.ts";

export type ExtractionState =
  | "idle"
  | "extracting"
  | "extracted"
  | "scoring"
  | "planning"
  | "awaiting_confirmation"
  | "executing"
  | "complete"
  | "declined"
  | "queued"
  | "error_input";

export interface ExtractedLeg {
  /** Per-leg sport (a slip may mix sports). Drives the scoring router. */
  sport?: "football" | "tennis" | "basketball" | "other" | string;
  home_team?: string;
  away_team?: string;
  league?: string;
  market?: string;
  selection?: string;
  odds?: number;
  outcome?: "pending";
  bookmaker?: string;
}

export interface ExtractionResult {
  extraction_id: string;
  extracted_legs: ExtractedLeg[];
  combined_odds: number | null;
}

export interface ExtractInput {
  input_mode: "upload" | "paste";
  slip_text: string;
  slip_image_path: string | null;
  slip_image_mime: string | null;
  slip_image_size_bytes: number | null;
  user_prompt: string;
  analysis?: "optimise" | "stress_test";
  stake?: number | null;
  slips_per_week?: number | null;
}

export interface SlipExtractionState {
  state: ExtractionState;
  result: ExtractionResult | null;
  /** SE-6 — populated after the `score` action returns. */
  scoredLegs: any[] | null;
  /** SE-6 — populated after the `parse_intent` action returns. */
  filterPlan: FilterPlan | null;
  /** SE-7 — populated after the `execute` action returns. */
  filterResult: FilterResult | null;
  /** Always-on liability radar — ticket-killer flags from the `score` action
   *  (expired/early matches, high-variance overs, no-conviction & weak legs). */
  liabilities: any[] | null;
  analysisDegraded: boolean;
  degradedLegCount: number;
  stressReport: StressTestReport | null;
  financialContext: { stake: number; slipsPerWeek: number } | null;
  /** Only set for state === 'error_input' (local validation). Never
   * a technical/network error. Brief N01. */
  inputError: string | null;
  extractionId: string | null;
  extract: (input: ExtractInput) => Promise<void>;
  /** Slice E — resume an existing extraction by id (e.g. the return hand-off
   *  from Slip Analysis). Loads its extracted_legs into the 'extracted' state so
   *  the user can score + prune — the score re-reads the swarm verdicts. */
  resumeExtraction: (extractionId: string) => Promise<void>;
  /** SE-6 — orchestrates score → parse_intent. Sets state through
   *  scoring → planning → awaiting_confirmation. Pass `legsOverride`
   *  to score the user-corrected legs from the editable table. */
  scoreAndPlan: (legsOverride?: ExtractedLeg[]) => Promise<void>;
  /** SE-6 — user cancels at the plan card. */
  cancelPlan: () => void;
  /** SE-7 — confirms the plan + invokes execute. Stub here. */
  runPlan: () => Promise<void>;
  reset: () => void;
}

const MIN_PROMPT_LEN = 5;
const MIN_PASTE_LEN = 10;
const EDGE_TIMEOUT_MS = 120_000; // vision + tiling + fixture-resolve can exceed 60s (logs show 60-63s)
const SCORE_TIMEOUT_MS = 45_000;

function validateInput(input: ExtractInput): string | null {
  const prompt = (input.user_prompt ?? "").trim();
  if (prompt.length < MIN_PROMPT_LEN) {
    return "Tell STRIKZA what to do with this slip (at least 5 characters).";
  }
  if (input.input_mode === "upload") {
    if (!input.slip_image_path) {
      return "Upload a slip image before running the extraction.";
    }
    return null;
  }
  // paste mode
  const text = (input.slip_text ?? "").trim();
  if (text.length < MIN_PASTE_LEN) {
    return "Paste your slip text before running the extraction.";
  }
  if (text.length > 12_000) {
    return "Slip text is unusually long. Trim to the actual legs and retry.";
  }
  return null;
}

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return await new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

// The platform gateway severs long Edge-fn connections at ~60s, but the
// function keeps running and persists its result to the slip_extractions row.
// (Verified: a 37-leg extract dies on the wire at ~61s yet still writes 37 legs
// to the row.) On a connection cut we poll the row for that persisted result
// instead of dropping the user into the dead-end "queued" state. A genuine
// server failure flips status to queued/failed/declined, which we honour.
const POLL_INTERVAL_MS = 3_000;
const POLL_BUDGET_MS = 150_000;

async function pollForPersisted(
  supabase: ReturnType<typeof getBrowserClient>,
  opts: {
    extractionId?: string | null;
    userId?: string | null;
    sinceIso?: string | null;
    column: "extracted_legs" | "scored_legs";
    budgetMs?: number;
  },
): Promise<Record<string, any> | null> {
  const deadline = Date.now() + (opts.budgetMs ?? POLL_BUDGET_MS);
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    try {
      // slip_extractions is absent from the generated Database types, so we
      // match the established `as never` cast used elsewhere (useExtractionHistory).
      const cols = "extraction_id, extracted_legs, scored_legs, combined_odds_kept, status";
      const base = supabase.from("slip_extractions" as never).select(cols);
      const query = opts.extractionId
        ? base.eq("extraction_id" as never, opts.extractionId as never)
        : opts.userId && opts.sinceIso
          ? base
              .eq("user_id" as never, opts.userId as never)
              .gte("created_at" as never, opts.sinceIso as never)
              .order("created_at", { ascending: false })
              .limit(1)
          : null;
      if (!query) return null;
      const { data } = await query.maybeSingle();
      if (!data) continue;
      const row = data as any;
      const status = String(row.status ?? "");
      if (status === "queued" || status === "failed" || status === "declined") {
        return null; // genuine server-side outcome — honour it
      }
      const legs = row[opts.column];
      if (Array.isArray(legs) && legs.length > 0) return row;
    } catch {
      // transient read error — keep polling until the budget runs out
    }
  }
  return null;
}

async function pollForCompletedExecution(
  supabase: ReturnType<typeof getBrowserClient>,
  extractionId: string,
  budgetMs = 75_000,
): Promise<Record<string, any> | null> {
  const deadline = Date.now() + budgetMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    try {
      const { data } = await supabase
        .from("slip_extractions" as never)
        .select("status, scored_legs, selected_leg_indices")
        .eq("extraction_id" as never, extractionId as never)
        .maybeSingle();
      const row = data as any;
      if (row?.status === "complete" && Array.isArray(row.scored_legs)) return row;
      if (row?.status === "failed" || row?.status === "declined") return null;
    } catch {
      // Keep polling through transient database read failures.
    }
  }
  return null;
}

function countAgentUnavailableLegs(legs: any[]): number {
  return legs.filter(
    (leg) =>
      Array.isArray(leg?.drivers) &&
      leg.drivers.some((driver: any) => driver?.source === "agent_unavailable"),
  ).length;
}

export function useSlipExtraction(): SlipExtractionState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [state, setState] = useState<ExtractionState>("idle");
  const [result, setResult] = useState<ExtractionResult | null>(null);
  const [scoredLegs, setScoredLegs] = useState<any[] | null>(null);
  const [filterPlan, setFilterPlan] = useState<FilterPlan | null>(null);
  const [filterResult, setFilterResult] = useState<FilterResult | null>(null);
  const [liabilities, setLiabilities] = useState<any[] | null>(null);
  const [analysisDegraded, setAnalysisDegraded] = useState(false);
  const [degradedLegCount, setDegradedLegCount] = useState(0);
  const [stressReport, setStressReport] = useState<StressTestReport | null>(null);
  const [requestContext, setRequestContext] = useState<{
    analysis: "optimise" | "stress_test";
    stake: number | null;
    slipsPerWeek: number | null;
  }>({ analysis: "optimise", stake: null, slipsPerWeek: null });
  const [inputError, setInputError] = useState<string | null>(null);
  const [extractionId, setExtractionId] = useState<string | null>(null);
  const extractInFlightRef = useRef(false);

  const extract = useCallback(
    async (input: ExtractInput): Promise<void> => {
      if (extractInFlightRef.current) return;
      if (!user) {
        setState("error_input");
        setInputError("Sign in to run an extraction.");
        return;
      }

      // ── Local input validation ──
      const validationErr = validateInput(input);
      if (validationErr) {
        setState("error_input");
        setInputError(validationErr);
        return;
      }

      extractInFlightRef.current = true;
      try {
        setState("extracting");
        setInputError(null);
        setResult(null);
        setStressReport(null);
        setRequestContext({
          analysis: input.analysis ?? "optimise",
          stake: input.stake ?? null,
          slipsPerWeek: input.slips_per_week ?? null,
        });

        // Correlate recovery to this exact run. A prior implementation searched
        // for the user's newest row, so a quick retry could hide a completed OCR
        // result behind a newer empty processing row.
        const requestExtractionId = crypto.randomUUID();

        // ── Call the slip-extract Edge fn ──
        try {
          const { data, error: invokeErr } = await withTimeout(
            supabase.functions.invoke<{
              ok: boolean;
              extraction_id?: string;
              extracted_legs?: ExtractedLeg[];
              combined_odds?: number | null;
              error?: string;
            }>("slip-extract", {
              body: {
                action: "extract",
                extraction_id: requestExtractionId,
                input_mode: input.input_mode,
                slip_text: input.slip_text,
                slip_image_path: input.slip_image_path,
                slip_image_mime: input.slip_image_mime,
                slip_image_size_bytes: input.slip_image_size_bytes,
                user_prompt: input.user_prompt,
              },
            }),
            EDGE_TIMEOUT_MS,
          );

          if (!invokeErr && data?.ok) {
            setResult({
              extraction_id: data.extraction_id ?? "",
              extracted_legs: data.extracted_legs ?? [],
              combined_odds: data.combined_odds ?? null,
            });
            setExtractionId(data.extraction_id ?? null);
            setState("extracted");
            return;
          }
          // eslint-disable-next-line no-console
          console.warn(
            "[slip-extract] Edge fn returned error; attempting poll-recovery.",
            invokeErr?.message ?? data?.error ?? "unknown",
          );
        } catch (err) {
          // ~60s gateway cut, timeout, network — the server keeps running and
          // persists. Fall through to poll-recovery before giving up.
          // eslint-disable-next-line no-console
          console.warn(
            "[slip-extract] Edge fn threw; attempting poll-recovery.",
            err instanceof Error ? err.message : String(err),
          );
        }

        // The connection died but the Edge fn persists the legs on its own.
        // Poll the row the server inserted for this run and pick the result up.
        const row = await pollForPersisted(supabase, {
          extractionId: requestExtractionId,
          column: "extracted_legs",
        });
        if (row) {
          const legs = (row.extracted_legs ?? []) as ExtractedLeg[];
          const product = legs.reduce(
            (acc, l) => acc * (Number(l.odds) > 1 ? Number(l.odds) : 1),
            1,
          );
          setResult({
            extraction_id: String(row.extraction_id),
            extracted_legs: legs,
            combined_odds: legs.length ? Number(product.toFixed(2)) : null,
          });
          setExtractionId(String(row.extraction_id));
          setState("extracted");
          return;
        }
        setState("queued");
      } finally {
        extractInFlightRef.current = false;
      }
    },
    [supabase, user],
  );

  // Slice E — resume an existing extraction (the return hand-off from Slip
  // Analysis lands here with ?extraction=<id>). Load its legs into the
  // 'extracted' review state so the user can score + prune; scoring re-reads the
  // swarm verdicts for a swarm-informed cut.
  const resumeExtraction = useCallback(
    async (id: string): Promise<void> => {
      if (!id) return;
      setState("extracting");
      setInputError(null);
      setResult(null);
      try {
        const { data } = await supabase
          .from("slip_extractions" as never)
          .select("extraction_id, extracted_legs")
          .eq("extraction_id" as never, id as never)
          .maybeSingle();
        const row = data as { extraction_id?: string; extracted_legs?: ExtractedLeg[] } | null;
        const legs = Array.isArray(row?.extracted_legs) ? row!.extracted_legs : [];
        if (!row?.extraction_id || legs.length === 0) {
          setState("queued");
          return;
        }
        const product = legs.reduce((acc, l) => acc * (Number(l.odds) > 1 ? Number(l.odds) : 1), 1);
        setResult({
          extraction_id: row.extraction_id,
          extracted_legs: legs,
          combined_odds: legs.length ? Number(product.toFixed(2)) : null,
        });
        setExtractionId(row.extraction_id);
        setState("extracted");
      } catch {
        setState("queued");
      }
    },
    [supabase],
  );

  const scoreAndPlan = useCallback(
    async (legsOverride?: ExtractedLeg[]): Promise<void> => {
      if (!extractionId) return;

      // ── 1. score ────────────────────────────────────────────────
      setState("scoring");
      let scored: any[] | null = null;
      try {
        // When the user corrected names in the editable legs table, send those
        // legs so the server re-resolves fixtures + scores the corrected slip.
        const scoreBody: Record<string, unknown> = {
          action: "score",
          extraction_id: extractionId,
        };
        if (Array.isArray(legsOverride) && legsOverride.length > 0) {
          scoreBody.legs = legsOverride;
        }
        const { data, error: invokeErr } = await withTimeout(
          supabase.functions.invoke<{
            ok: boolean;
            scored_legs?: any[];
            liabilities?: any[];
            analysis_degraded?: boolean;
            degraded_leg_count?: number;
            error?: string;
          }>("slip-extract", {
            body: scoreBody,
          }),
          SCORE_TIMEOUT_MS,
        );
        if (!invokeErr && data?.ok) {
          scored = data.scored_legs ?? [];
          setScoredLegs(scored);
          setLiabilities(Array.isArray(data.liabilities) ? data.liabilities : null);
          setAnalysisDegraded(data.analysis_degraded === true);
          setDegradedLegCount(Number(data.degraded_leg_count ?? 0));
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            "[slip-extract:score] Edge fn error; attempting poll-recovery.",
            invokeErr?.message ?? data?.error ?? "unknown",
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          "[slip-extract:score] threw; attempting poll-recovery.",
          err instanceof Error ? err.message : String(err),
        );
      }

      // A cold 37-leg score (on-demand pricing + agent swarm + web-research
      // estimates) can also overrun the ~60s gateway cut. The server still
      // persists scored_legs to the row, so recover from there rather than
      // dead-ending. (Liabilities aren't persisted, so the radar is simply
      // omitted on a recovered score — far better than a queued screen.)
      if (scored === null) {
        const row = await pollForPersisted(supabase, {
          extractionId,
          column: "scored_legs",
          budgetMs: 45_000,
        });
        if (Array.isArray(row?.scored_legs) && row!.scored_legs.length > 0) {
          scored = row!.scored_legs;
          const degraded = countAgentUnavailableLegs(scored);
          setScoredLegs(scored);
          setLiabilities(null);
          setAnalysisDegraded(degraded > 0);
          setDegradedLegCount(degraded);
        } else {
          setState("queued");
          return;
        }
      }

      // ── 2. parse_intent ─────────────────────────────────────────
      setState("planning");
      try {
        const { data, error: invokeErr } = await withTimeout(
          supabase.functions.invoke<{
            ok: boolean;
            filter_plan?: FilterPlan;
            error?: string;
          }>("slip-extract", {
            body: { action: "parse_intent", extraction_id: extractionId },
          }),
          EDGE_TIMEOUT_MS,
        );
        if (invokeErr || !data?.ok || !data.filter_plan) {
          // eslint-disable-next-line no-console
          console.warn(
            "[slip-extract:parse_intent] Edge fn error; user shown queued state.",
            invokeErr?.message ?? data?.error ?? "unknown",
          );
          setState("queued");
          return;
        }
        setFilterPlan(data.filter_plan);
        // N09 — honest refusal short-circuits to declined state.
        if (data.filter_plan.declined) {
          setState("declined");
        } else {
          setState("awaiting_confirmation");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          "[slip-extract:parse_intent] threw; user shown queued state.",
          err instanceof Error ? err.message : String(err),
        );
        setState("queued");
      }
    },
    [supabase, extractionId],
  );

  const cancelPlan = useCallback((): void => {
    setFilterPlan(null);
    setState("extracted");
  }, []);

  const runPlan = useCallback(async (): Promise<void> => {
    // SE-7 — invoke the `execute` action server-side.
    if (!filterPlan || !extractionId) return;
    setState("executing");
    try {
      const { data, error: invokeErr } = await withTimeout(
        supabase.functions.invoke<{
          ok: boolean;
          result?: FilterResult;
          error?: string;
        }>("slip-extract", {
          body: { action: "execute", extraction_id: extractionId },
        }),
        EDGE_TIMEOUT_MS,
      );
      if (invokeErr || !data?.ok || !data.result) {
        // eslint-disable-next-line no-console
        console.warn(
          "[slip-extract:execute] Edge fn error; user shown queued state.",
          invokeErr?.message ?? data?.error ?? "unknown",
        );
        const recovered = await pollForCompletedExecution(supabase, extractionId);
        if (recovered) {
          setFilterResult(applyFilterPlan(recovered.scored_legs, filterPlan));
          setState("complete");
          return;
        }
        setState("queued");
        return;
      }
      setFilterResult(data.result);
      if (requestContext.analysis === "stress_test") {
        try {
          const { data: stress } = await withTimeout(
            supabase.functions.invoke<{
              ok: boolean;
              stress_report?: StressTestReport;
            }>("slip-extract", {
              body: { action: "stress_test", extraction_id: extractionId },
            }),
            EDGE_TIMEOUT_MS,
          );
          if (stress?.ok && stress.stress_report) {
            setStressReport(stress.stress_report);
          }
        } catch {
          setStressReport(null);
        }
      }
      // N09 — declined plans land in 'declined' rather than 'complete'.
      setState(data.result.declined ? "declined" : "complete");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[slip-extract:execute] threw; user shown queued state.",
        err instanceof Error ? err.message : String(err),
      );
      const recovered = await pollForCompletedExecution(supabase, extractionId);
      if (recovered) {
        setFilterResult(applyFilterPlan(recovered.scored_legs, filterPlan));
        setState("complete");
        return;
      }
      setState("queued");
    }
  }, [supabase, filterPlan, extractionId, requestContext.analysis]);

  const reset = useCallback((): void => {
    setState("idle");
    setResult(null);
    setScoredLegs(null);
    setFilterPlan(null);
    setFilterResult(null);
    setLiabilities(null);
    setAnalysisDegraded(false);
    setDegradedLegCount(0);
    setStressReport(null);
    setRequestContext({ analysis: "optimise", stake: null, slipsPerWeek: null });
    setInputError(null);
    setExtractionId(null);
  }, []);

  return useMemo<SlipExtractionState>(
    () => ({
      state,
      result,
      scoredLegs,
      filterPlan,
      filterResult,
      liabilities,
      analysisDegraded,
      degradedLegCount,
      stressReport,
      financialContext:
        requestContext.stake && requestContext.slipsPerWeek
          ? { stake: requestContext.stake, slipsPerWeek: requestContext.slipsPerWeek }
          : null,
      inputError,
      extractionId,
      extract,
      resumeExtraction,
      scoreAndPlan,
      cancelPlan,
      runPlan,
      reset,
    }),
    [
      state,
      result,
      scoredLegs,
      filterPlan,
      filterResult,
      liabilities,
      analysisDegraded,
      degradedLegCount,
      stressReport,
      requestContext,
      inputError,
      extractionId,
      extract,
      resumeExtraction,
      scoreAndPlan,
      cancelPlan,
      runPlan,
      reset,
    ],
  );
}
